import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import React from 'react';
import { attemptedSections, liveSectionErrors } from '../../domain/setup/liveSetupRules';
import type { LiveSetupStepProps } from './liveSetupStepProps';
import OfferDocumentsCard from './OfferDocumentsCard';
import LiveSetupBlock from './LiveSetupBlock';
import { BrandingPreview, CertificatePreview } from './SetupPreviews';

/** Server errors win; rule errors show once the field is left after editing, or after an attempt. */
export function visibleFieldErrors({
  section,
  values,
  readOnly,
  fieldErrors,
  attempts,
  touched,
}: Pick<
  LiveSetupStepProps,
  'section' | 'values' | 'readOnly' | 'fieldErrors' | 'attempts' | 'touched'
>): Record<string, string> {
  const marked = attemptedSections(attempts).includes(section.id);
  const ruleErrors = readOnly ? {} : liveSectionErrors(section.id, values);
  const shown = Object.fromEntries(
    Object.entries(ruleErrors).filter(([key]) => marked || touched.includes(key)),
  );
  return { ...shown, ...fieldErrors };
}

export default function LiveSetupFieldStep(props: LiveSetupStepProps) {
  const { setup, setupId, section, values, readOnly, busy, session, action } = props;
  const errors = visibleFieldErrors(props);
  const marked = attemptedSections(props.attempts).includes(section.id);
  const missingCount = Object.keys(liveSectionErrors(section.id, values)).length;
  const preview =
    section.id === 'branding' ? (
      <BrandingPreview values={{ ...values, 'brand.logoUrl': setup.logoUrl ?? '' }} />
    ) : section.id === 'class' ? (
      <CertificatePreview values={values} />
    ) : null;

  const forms = (
    <Stack spacing={2}>
      {section.blocks.map((block) => (
        <LiveSetupBlock key={block.title} {...props} block={block} errors={errors} />
      ))}
      {section.id === 'offer' && (
        <OfferDocumentsCard
          setup={setup}
          setupId={setupId}
          readOnly={readOnly}
          busy={busy}
          session={session}
          action={action}
        />
      )}
    </Stack>
  );

  return (
    <>
      {marked && !readOnly && missingCount > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {missingCount === 1 ? 'One field needs' : `${missingCount} fields need`} attention on this
          step.
        </Alert>
      )}
      {preview ? (
        <Box
          sx={{
            display: 'grid',
            gap: 2,
            gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) 340px' },
          }}
        >
          {forms}
          <Box sx={{ position: { lg: 'sticky' }, top: { lg: 88 }, alignSelf: 'start' }}>
            <Typography variant="overline" color="text.secondary">
              Live preview
            </Typography>
            {preview}
            {section.id === 'branding' && (
              <Button disabled sx={{ mt: 2 }}>
                Get logo from website (coming soon)
              </Button>
            )}
          </Box>
        </Box>
      ) : (
        forms
      )}
    </>
  );
}
